<template>
  <el-container v-if="show" direction="vertical" class="survey-question">
    <transition :name="transitionName">
      <router-view v-if="showQuestionContainer" :currentQuestion="currentQuestion" :allQuestions="allQuestions" :currentStep="currentStep" @selectedChange="handleSelectedChange"></router-view>
    </transition>
    <el-footer class="question-footer" height="auto">
      <div class="btn-group">
        <div
          class="back-btn"
          :style="customBackBtnStyle"
          @click="handleBackBtnClick">
          {{ $t('survey.btn.back') }}
        </div>
        <div
          class="next-btn"
          :style="customNextBtnStyle"
          @click="handleNextBtnClick">
          {{ $t(`survey.btn.${currentStep === allQuestions.length ? 'finish' : 'next'}`) }}
        </div>
      </div>
    </el-footer>
  </el-container>
</template>

<script>
import { mapActions } from 'vuex'
import _ from 'lodash'

export default {
  props: {
    surveyInfo: {
      type: Object,
      default: () => {}
    },
    isMSDCustom: {
      type: Boolean,
      default: false
    },
    customCompanyId: {
      required: false
    }
  },
  data () {
    return {
      show: false,
      allQuestions: [],
      customBackBtnStyle: {},
      customNextBtnStyle: {},
      showQuestionContainer: true,
      transitionName: 'slide-left',
      currentStep: this.$route.params.step,
      selectedDisplayValue: ''
    }
  },
  computed: {
    companyId () {
      return this.customCompanyId || this.$route.params.companyId
    },
    currentQuestion () {
      return this.allQuestions[this.currentStep - 1]
    },
    companyCustomColor () {
      return this.surveyInfo.company && this.surveyInfo.company.color
    }
  },
  created () {
    this.initQuestionData()
    this.currentStep = parseInt(this.$route.params.step)
  },
  methods: {
    ...mapActions('survey', ['getSurveyQuestion', 'answerSurveyQuestion']),
    async initQuestionData () {
      this.show = false
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.getSurveyQuestion(this.companyId)
        if (resp) {
          this.allQuestions = resp.questions
        }
        this.show = true
        this.customBackBtnStyle = {
          borderColor: this.companyCustomColor,
          color: this.companyCustomColor
        }
        this.customNextBtnStyle = {
          backgroundColor: this.companyCustomColor,
          color: this.getTextColorByBgColor(this.companyCustomColor)
        }
        this.checkStep()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getTextColorByBgColor (bgColor) {
      bgColor = bgColor.replace('#', '')
      const r = parseInt(bgColor.substr(0, 2), 16)
      const g = parseInt(bgColor.substr(2, 2), 16)
      const b = parseInt(bgColor.substr(4, 2), 16)
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000
      return (yiq >= 160) ? '#000' : '#fff'
    },
    checkStep () {
      let currentStep = this.currentStep
      if (currentStep > this.allQuestions.length) {
        currentStep = this.allQuestions.length
      }
      const noValueIndex = _.findIndex(this.allQuestions, (item) => {
        const value = item.result
        return value === null || value === undefined || value === ''
      })
      const currentIndex = currentStep - 1
      if (noValueIndex > -1 && noValueIndex < currentIndex) {
        if (this.isMSDCustom) {
          this.$router.replace({
            path: `/msdchina/join-talent-community/question/${noValueIndex + 1}`,
            query: this.$route.query
          })
        } else {
          this.$router.replace({
            path: `/recruiting/company/${this.companyId}/survey/question/${noValueIndex + 1}`,
            query: this.$route.query
          })
        }
        this.currentStep = noValueIndex + 1
      } else if (this.currentStep > this.allQuestions.length) {
        if (this.isMSDCustom) {
          this.$router.replace({
            path: `/msdchina/join-talent-community/question/${currentStep}`,
            query: this.$route.query
          })
        } else {
          this.$router.replace({
            path: `/recruiting/company/${this.companyId}/survey/question/${currentStep}`,
            query: this.$route.query
          })
        }
        this.currentStep = currentStep
      }
    },
    validate () {
      const question = this.currentQuestion
      const result = question.result
      if (!result) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('survey.message.noAnswerQuestion')
        })
        return false
      }
      return true
    },
    switchAnimation (isBack) {
      if (isBack) {
        this.transitionName = 'slide-right'
      } else {
        this.transitionName = 'slide-left'
      }
      this.$nextTick(() => {
        this.showQuestionContainer = false
        if (isBack) {
          this.$router.back()
          this.currentStep -= 1
        } else {
          if (this.isMSDCustom) {
            this.$router.push({
              path: `/msdchina/join-talent-community/question/${this.currentStep + 1}`,
              query: this.$route.query
            })
          } else {
            this.$router.push({
              path: `/recruiting/company/${this.companyId}/survey/question/${this.currentStep + 1}`,
              query: this.$route.query
            })
          }
          this.currentStep += 1
        }
        setTimeout(() => {
          this.showQuestionContainer = true
        }, 200)
      })
    },
    handleBackBtnClick () {
      if (this.currentStep === 1) {
        if (this.isMSDCustom) {
          this.$router.push({
            path: '/msdchina/join-talent-community/preference',
            query: this.$route.query
          })
          return
        } else {
          this.$router.push({
            path: `/recruiting/company/${this.companyId}/survey/preference`,
            query: this.$route.query
          })
          return
        }
      }
      this.switchAnimation(true)
    },
    handleSelectedChange (selectedDisplayValue) {
      this.selectedDisplayValue = selectedDisplayValue
    },
    async handleNextBtnClick () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const result = this.validate()
        if (!result) {
          return
        }
        const question = {}
        _.forEach(this.currentQuestion, (value, key) => {
          if (key !== 'options') {
            question[key] = value
          }
        })
        if (this.currentQuestion.question_type !== 'text') {
          question.option_desc = this.selectedDisplayValue
        }
        const resp = await this.answerSurveyQuestion({
          companyId: this.companyId,
          question
        })
        if (resp) {
          const nextStep = this.currentStep + 1
          if (nextStep > this.allQuestions.length) {
            const isFirst = !this.surveyInfo.survey.has_answered
            const companyId = this.surveyInfo.company.root_company_id || this.companyId
            const path = this.isMSDCustom ? '/msdchina/join-talent-community/preference' : `/recruiting/company/${companyId}/survey/`
            this.surveyInfo.survey.has_answered = true
            if (isFirst || !this.surveyInfo.has_resume_file) {
              this.$router.push({
                path: '/recruiting/personal/resume/add',
                query: {
                  is_from_preference: 1,
                  only_upload_resume: 1,
                  parse_resume: 1,
                  company_id: companyId,
                  back: `${path}?is_finish=true${isFirst ? '&is_first=true' : ''}`
                }
              })
              this.currentStep--
            } else {
              this.$router.push({
                path: path,
                query: {
                  ...this.$route.query,
                  is_finish: 'true',
                  is_first: isFirst
                }
              })
            }
          } else {
            this.switchAnimation(false)
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-question {
  overflow: hidden;

  ::v-deep .question-container {
    transition: transform .3s ease;
  }

  .slide-left-enter {
    transform: translateX(100%);
  }

  .slide-left-leave-to {
    transform: translateX(-100%);
  }

  .slide-right-enter {
    transform: translateX(-100%);
  }

  .slide-right-leave-to {
    transform: translateX(100%);
  }

  .question-footer {
    padding: 20px 45px;

    .btn-group {
      display: flex;
      height: 30px;
      line-height: 18px;

      .back-btn {
        width: 80px;
        margin-left: 0;
        margin-right: auto;
        padding: 5px;
        border: 1px solid #000;
        text-align: center;
        cursor: pointer;
      }

      .next-btn {
        width: 80px;
        margin-left: auto;
        margin-right: 0;
        padding: 5px;
        text-align: center;
        cursor: pointer;
      }
    }
  }
}

.is-mobile {
  .question-footer {
    padding: 20px;

    .btn-group {
      height: 25px;
      line-height: 15px;
      font-size: 14px;

      .back-btn {
        width: 45px;
      }

      .next-btn {
        width: 45px;
      }
    }
  }
}
</style>
